import { EmptyState } from '@/features/shop/components/EmptyState'
import { UngatedFilterOptions } from '@/features/shop/components/UngatedCategoryPage/UngatedFilterOptions'
import { SkeletonProvider } from '@/features/shared/components/SkeletonLoader'
import { useMarketingZip } from '@/features/shop/components/UngatedCategoryPage/hooks'
import { UngatedProductGrid } from '@/features/shop/components/UngatedCategoryPage/UngatedProductGrid'
import { normalizeFacets } from '@/features/shop/services/Search/utils'
import { useQueryUniversalSearch } from '@/features/shop/services/UniversalCategories/queries'
import { useCurrentUniversalCategory } from '@/features/shop/services/UniversalCategories/hooks'
import { getParentCategory } from '@/features/shop/services/UniversalCategories/utils'
import { screenSizes } from '@shipt/design-system-themes'
import styled from 'styled-components'
import { usePathname, useSearchParams } from 'next/navigation'
import { Box } from '@shipt/design-system-layouts'
import { Pagination } from '@/features/shared/components/Pagination'
import { getSearchQueryString } from '@/features/shared/utils/url'

export const ProductGridWrapper = ({ className }: { className?: string }) => {
  const pathname = usePathname()

  const params = useSearchParams()
  const pageParam = params?.get('page')
  const pageAsNumber = pageParam && !isNaN(+pageParam) ? +pageParam : 1

  const generatePageUrl = (targetPage: number) => {
    const newParams = new URLSearchParams(params ?? undefined)
    newParams.set('page', targetPage.toString())

    const query = getSearchQueryString(newParams)
    return `${pathname}${query}`
  }
  const zip = useMarketingZip()
  const { isFetching, data: productData } = useQueryUniversalSearch(zip ? { zip } : undefined)

  const pageCategory = useCurrentUniversalCategory()
  const parentCategory = getParentCategory(pageCategory)
  const facets = normalizeFacets(productData?.facets ?? [])

  const additionalText = zip ? `in ${zip}` : ''
  const ITEMS_PER_PAGE = 30
  const MAX_SEARCH_QUERY = 500 // search API errors when requesting more than the 500th product
  const MAX_SAFE_PAGES = Math.floor(MAX_SEARCH_QUERY / ITEMS_PER_PAGE) - 1

  return (
    <Wrapper className={className}>
      <FilterWrapper>
        <FilterOptions
          facets={facets}
          category={
            pageCategory?.level === 1 || pageCategory?.level === 2 ? pageCategory : parentCategory
          }
        />
      </FilterWrapper>
      {productData?.products ? (
        <>
          <SkeletonProvider isLoading={isFetching}>
            <UngatedProductGrid data={productData.products} />
          </SkeletonProvider>
          <PaginationWrapper marginTop="xl">
            <Pagination
              currentPage={pageAsNumber}
              shallow
              generatePageUrl={generatePageUrl}
              totalPages={Math.min(
                Math.ceil(productData.total_hits / productData.hits_per_page),
                MAX_SAFE_PAGES,
              )}
              suppressBoundaries
            />
          </PaginationWrapper>
        </>
      ) : (
        <EmptyStateContainer>
          <EmptyState
            text={`No ${pageCategory?.name} items available ${additionalText}`}
            subText="Try a new ZIP code or check back soon."
            hideButton
          />
        </EmptyStateContainer>
      )}
    </Wrapper>
  )
}

const PaginationWrapper = styled(Box)`
  place-self: end;
`

const Wrapper = styled.div`
  min-width: 0;
  position: relative;
`

const FilterWrapper = styled.div`
  align-items: center;

  @media ${screenSizes.smDesktop} {
    display: grid;
    grid-template-columns: 1fr auto;
  }
`

const FilterOptions = styled(UngatedFilterOptions)`
  padding: 16px 16px 8px 0;
  margin-bottom: 20px;

  @media ${screenSizes.tablet} {
    padding-top: 8px;
    margin-bottom: 0;
  }
`

const EmptyStateContainer = styled.div`
  @media ${screenSizes.tablet} {
    margin-left: auto;
    margin-right: auto;
  }

  margin-top: 50px;
`
